import { CircleNotchIcon, type IconProps } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import React, { cloneElement, isValidElement } from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const iconButtonVariants = cva(
  "inline-flex items-center justify-center rounded-md transition-colors cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-violet-800 text-white hover:bg-violet-700 active:bg-violet-900",
        outline:
          "border border-gray-200 bg-background text-slate-800 hover:bg-slate-100 active:bg-slate-200 shadow-xs",
        ghost:
          "bg-transparent text-slate-800 hover:bg-slate-100 active:bg-slate-200",
        destructive: "bg-red-800 text-white hover:bg-red-700 active:bg-red-900",
        secondaryDestructive:
          "bg-red-750 text-white hover:bg-red-600 active:bg-red-800 data-hovered:bg-red-600 data-pressed:bg-red-800",
        "icon-only": "text-gray-500 hover:text-gray-700 bg-transparent",
      },
      size: {
        sm: "w-6 h-6 p-1.5",
        md: "w-7 h-7 p-1.5",
        lg: "w-12 h-12 p-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

const iconSizes = {
  sm: 16,
  md: 20,
  lg: 24,
} as const;

interface IconButtonProps
  extends Omit<AriaButtonProps, "children">,
    VariantProps<typeof iconButtonVariants> {
  /**
   * Phosphor icon element
   * @example icon={<Plus />}
   */
  icon: React.ReactElement<IconProps>;
  /**
   * Optional label for accessibility
   */
  "aria-label": string;
  /**
   * Optional class name for custom styling
   */
  className?: string;
  /**
   * Optional label for tooltip
   */
  tooltipLabel?: string;
  /**
   * Optional position for tooltip
   */
  tooltipPosition?: "top" | "bottom" | "left" | "right";
  /**
   * Optional offset for tooltip
   */
  offset?: number;
  /**
   * Optional cross offset for tooltip
   */
  crossOffset?: number;
  /**
   * Whether the button is in a pending state
   */
  isPending?: boolean;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      size,
      variant,
      className,
      "aria-label": ariaLabel,
      tooltipLabel,
      tooltipPosition,
      isPending = false,
      ...props
    },
    ref,
  ) => {
    if (!isValidElement(icon)) {
      console.error(
        "IconButton requires a valid React element as the 'icon' prop.",
      );
      return null;
    }

    const sizedIcon = isPending ? (
      <CircleNotchIcon
        size={size ? iconSizes[size] : icon.props.size}
        className="animate-spin"
      />
    ) : (
      cloneElement(icon, {
        size: icon.props.size || (size ? iconSizes[size] : undefined),
      })
    );

    const button = (
      <AriaButton
        ref={ref}
        className={cn(iconButtonVariants({ variant, size }), className)}
        aria-label={ariaLabel}
        {...props}
      >
        {sizedIcon}
      </AriaButton>
    );

    if (tooltipLabel) {
      return (
        <TooltipTrigger>
          {button}
          <Tooltip
            placement={tooltipPosition}
            offset={props.offset}
            crossOffset={props.crossOffset}
          >
            {tooltipLabel}
          </Tooltip>
        </TooltipTrigger>
      );
    }

    return button;
  },
);

IconButton.displayName = "IconButton";

export type { IconButtonProps };
export { IconButton };
export default IconButton;
